import type { RefObject } from "react";
import { useMemo, useRef, useState, useEffect } from "react";
import type { MonthProperties } from "./month";
import { Month } from "./month";
import type { CalendarDate } from "@internationalized/date";
import { startOfMonth } from "@internationalized/date";
import { DaysOfWeek } from "./days-of-week";
import { IcChevronRight } from "@jetblue/kit-icons/ic-chevron-right";
import { IcChevronLeft } from "@jetblue/kit-icons/ic-chevron-left";
import { useCalendarHandle } from "./use-calendar-handle";
import type { CalendarHandle } from "./use-calendar-handle";
import type { RenderDate } from "./day";

export type { DayRenderProperties, RenderDate } from "./day";
export const useSnapScroll = <T extends HTMLElement | null>(
  containerReference: RefObject<T>,
) => {
  const getRect = (container: HTMLElement) =>
    container.querySelector("[data-month]")?.getBoundingClientRect();

  const stepForward = () => {
    if (containerReference.current) {
      containerReference.current.scrollBy({
        behavior: "smooth",
        left: getRect(containerReference.current)?.width,
      });
    }
  };
  const stepBack = () => {
    if (containerReference.current) {
      containerReference.current.scrollBy({
        behavior: "smooth",
        left: -1 * (getRect(containerReference.current)?.width ?? 0),
      });
    }
  };

  return {
    stepBack,
    stepForward,
  };
};
export function useScrollEdges<T extends HTMLElement>(
  containerReference: RefObject<T | null>,
  tolerance = 10,
) {
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const element = containerReference.current;
    if (!element) {
      return;
    }

    const checkEdges = () => {
      const start = element.scrollLeft <= tolerance;
      const end =
        element.scrollLeft + element.clientWidth >=
        element.scrollWidth - tolerance;
      setAtStart(start);
      setAtEnd(end);
    };

    const onScroll = () => {
      requestAnimationFrame(() => {
        checkEdges();
      });
    };
    checkEdges();
    element.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", checkEdges);

    return () => {
      element.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", checkEdges);
    };
  }, [containerReference, tolerance]);

  return { atEnd, atStart };
}
export type CalendarLabels = {
  nextMonth: string;
  prevMonth: string;
};
export type CalendarProperties = {
  calendarRef?: RefObject<CalendarHandle | null>;
  startDate: CalendarDate;
  endDate: CalendarDate;
  rangeStart?: CalendarDate;
  rangeEnd?: CalendarDate;
  onDateClick?: ((date: CalendarDate) => void) | undefined;
  labels?: CalendarLabels | undefined;
  renderDate?: RenderDate;
} & Pick<MonthProperties, "highlightRange">;
export const getMonthsBetween = (start: CalendarDate, end: CalendarDate) => {
  const months: CalendarDate[] = [];

  let cursor = startOfMonth(start);
  const lastMonth = startOfMonth(end);
  while (cursor.compare(lastMonth) <= 0) {
    months.push(cursor);
    cursor = cursor.add({ months: 1 }); // move forward 1 month
  }
  return months;
};
export const Calendar = ({
  highlightRange = false,
  calendarRef,
  startDate,
  endDate,
  rangeStart,
  rangeEnd,
  onDateClick,
  labels,
  renderDate,
}: CalendarProperties) => {
  const containerReference = useRef<HTMLDivElement>(null);

  const { atStart, atEnd } = useScrollEdges(containerReference);
  const { stepBack, stepForward } = useSnapScroll(containerReference);
  const months: CalendarDate[] = useMemo(
    () => getMonthsBetween(startDate, endDate),
    [startDate, endDate],
  );

  useCalendarHandle(calendarRef, containerReference, months);

  return (
    <div className="relative isolate flex flex-col overflow-scroll">
      <div className="sticky top-0 z-10 w-full bg-white md:hidden">
        <div className="h-1 bg-light-gray"></div>
        <div className="px-4 py-2 text-body text-slate-gray">
          <DaysOfWeek />
        </div>
        <div className="h-1 bg-light-gray"></div>
      </div>
      <div
        className="group/scroll flex flex-col divide-y-4 divide-light-gray md:snap-x md:snap-mandatory md:flex-row md:divide-y-0 md:overflow-x-auto"
        data-at-end={atEnd}
        data-at-start={atStart}
        ref={containerReference}
        tabIndex={-1}
      >
        {months.map((month) => (
          <div
            className="md:flex-none md:snap-start"
            data-month
            key={month.toString()}
          >
            <Month
              endDate={endDate}
              highlightRange={highlightRange}
              month={month}
              onDateClick={onDateClick}
              rangeEnd={rangeEnd}
              rangeStart={rangeStart}
              renderDate={renderDate}
              startDate={startDate}
            />
          </div>
        ))}
      </div>
      <button
        aria-label={labels?.prevMonth ?? "previous month"}
        className="absolute start-0.5 top-1/2 hidden rounded-full p-1.5 text-royal-blue transition-all focus-within:bg-off-white hover:bg-off-white focus-visible:outline focus-visible:outline-royal-blue data-[at-start=true]:opacity-0 md:block"
        data-at-start={atStart}
        onClick={stepBack}
        onPointerDown={(event) => {
          event.preventDefault();
        }}
        tabIndex={-1}
        type="button"
      >
        <IcChevronLeft />
      </button>
      <button
        aria-label={labels?.nextMonth ?? "next month"}
        className="absolute end-0.5 top-1/2 hidden rounded-full p-1.5 text-royal-blue transition-all focus-within:bg-off-white hover:bg-off-white focus-visible:outline focus-visible:outline-royal-blue data-[at-start=true]:opacity-0 md:block"
        data-at-end={atEnd}
        onClick={stepForward}
        onPointerDown={(event) => {
          event.preventDefault();
        }}
        tabIndex={-1}
        type="button"
      >
        <IcChevronRight />
      </button>
    </div>
  );
};
Calendar.displayName = "Calendar";
